"use client";

import { getAccountSettingExternalUrls } from "@/app/members/(use-shared-layout)/account/setting/external-link";
import { TableHeadWithFontBold } from "@/components/Table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getOptionalContinuedCertificateUrl } from "@/constants/zendesk-urls/members-zendesk-urls";
import type { LatestLostInsuranceNumberIdsByMember } from "@/server/models/insurance-number/get-latest-lost-record";
import type { Insurer } from "@/server/models/insurer/insurer";
import { MemberEligibility } from "@/server/models/member/member-eligibility";
import { isOptionalInsuranceNumber } from "@/utils/format-insurance-number";
import ExternalLinkIcon from "@public/icons/external-link-icon.svg";
import InfoIcon from "@public/icons/info-icon.svg";
import Link from "next/link";
import { useState } from "react";
import { HistoryTable } from "./HistoryTable";
import { InformationSheet } from "./InformationSheet";
import type { InsuranceHistoryData } from "./types";

/**
 * 履歴の最新の資格喪失が任意継続かチェック
 */
const hasLatestLostOptionalContinued = (
  history: InsuranceHistoryData[],
): boolean => {
  // 資格喪失した履歴のみを抽出
  const lostHistory = history.filter((item) => item.lostAt !== null);

  if (lostHistory.length === 0) {
    return false;
  }

  // lostAtが最新のレコードを取得
  const latestLost = lostHistory.reduce((latest, current) => {
    return current.lostAt! > latest.lostAt! ? current : latest;
  });

  // 最新の資格喪失が任意継続かチェック
  return isOptionalInsuranceNumber(latestLost.number);
};

const NotificationOfEligibilityInformationLink = () => {
  return (
    <>
      「
      <Link
        href={"https://www.mhlw.go.jp/stf/newpage_50657.html"}
        className="text-primary-6 underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        資格情報のお知らせ
      </Link>
      」
    </>
  );
};

const OptionalContinuedCertificateAlert = () => {
  // 任意継続の資格喪失証明書のZendesk URLを取得
  const optionalContinuedCertificateLink = getOptionalContinuedCertificateUrl();

  return (
    <div className="flex items-center rounded-lg bg-primary-0.5 p-4">
      <InfoIcon className="mr-4 text-primary-5" />
      <p className="inline-flex text-sm text-primary-6">
        任意継続被保険者の資格喪失証明書の取得に関して
        <Link
          href={optionalContinuedCertificateLink}
          className="underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          資格喪失証明書交付申請書の申請方法
        </Link>
        をご確認ください。
      </p>
    </div>
  );
};

const Description = ({
  isPrimary,
  showLostEligibilityNote,
}: {
  isPrimary: boolean;
  showLostEligibilityNote: boolean;
}) => {
  return (
    <div className="space-y-4">
      <p className="text-sm">
        {isPrimary ? (
          <>
            ご自身と被扶養者の、VCスタートアップ健康保険組合に関する資格情報とその履歴です。
          </>
        ) : (
          <>ご自身の資格情報とその履歴です。</>
        )}
        <br />
        <NotificationOfEligibilityInformationLink />
        は、健康保険の資格（加入する保険者や記号番号等）を簡易に把握できる通知文書です。また、マイナ保険証と
        <NotificationOfEligibilityInformationLink />
        をセットで提示することで、オンライン資格確認義務化対象外の医療機関や、機器の故障等でマイナ保険証が利用できない場合でも保険診療が受けられます。
      </p>
      {showLostEligibilityNote && (
        <p className="text-sm">
          ※資格喪失証明書は、ダウンロード時に情報が最新へアップデートされますので、必要になった時にダウンロードしてご使用ください。
        </p>
      )}
    </div>
  );
};

const AddDependentButton = ({
  memberEligibility,
}: {
  memberEligibility: MemberEligibility;
}) => {
  const externalUrls = getAccountSettingExternalUrls({
    isPrimary: true,
    eligibility: memberEligibility,
  });
  if (!externalUrls.manageDependents) {
    return null;
  }

  return (
    <Button asChild>
      <Link
        href={externalUrls.manageDependents}
        prefetch={false}
        target="_blank"
        rel="noopener noreferrer"
        className="px-4 py-2 text-xs font-bold text-monotone-7"
      >
        被扶養家族の追加・削除
        <ExternalLinkIcon className="h-4 w-4 fill-monotone-9" />
      </Link>
    </Button>
  );
};

const DependentSection = ({
  dependentHistory,
  latestLostRecordIdsByMember,
  memberEligibility,
  enableAddDependent = true,
  showRelationshipNote = true,
  onHistoryClick,
  insurer,
}: {
  dependentHistory: InsuranceHistoryData[];
  latestLostRecordIdsByMember: LatestLostInsuranceNumberIdsByMember;
  memberEligibility: MemberEligibility;
  enableAddDependent?: boolean;
  showRelationshipNote?: boolean;
  onHistoryClick: (data: InsuranceHistoryData) => void;
  insurer: Insurer;
}) => {
  // 被保険者側でアラート表示されるため、重複を避けて非表示
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold">被扶養者</h3>
        {enableAddDependent && memberEligibility !== "lost_eligibility" && (
          <AddDependentButton memberEligibility={memberEligibility} />
        )}
      </div>
      {dependentHistory.length > 0 ? (
        <>
          <HistoryTable
            history={dependentHistory}
            latestLostRecordIdsByMember={latestLostRecordIdsByMember}
            isPrimary={false}
            onRowClick={onHistoryClick}
            insurer={insurer}
          />
          {showRelationshipNote && (
            <p className="text-sm text-monotone-9">
              ※続柄は被保険者から見た関係性を示すもの
            </p>
          )}
        </>
      ) : (
        <div className="items-center space-y-10 border-t pt-10">
          <div className="space-y-1 text-center">
            <div className="text-lg font-bold text-monotone-6">
              被扶養者が登録されていません。
            </div>
            {enableAddDependent && memberEligibility !== "lost_eligibility" && (
              <div className="text-sm text-monotone-6">
                ご家族を扶養に追加する場合は、「被扶養家族の追加・削除」から手続きを行ってください。
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const InsurerSection = ({ insurer }: { insurer: Insurer }) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold">保険者</h3>
      </div>
      <p className="text-sm">
        ご自身と被扶養者が加入している健康保険を運営している団体です。
        <br />
        医療機関等で「保険者・保険者番号」を聞かれた場合は、以下の情報をお伝えください。
      </p>
      <Table>
        <TableHeader className="h-14 bg-monotone-1 whitespace-nowrap md:whitespace-normal">
          <TableRow>
            <TableHeadWithFontBold className="min-w-48 text-sm md:w-2/5 md:max-w-none">
              保険者名
            </TableHeadWithFontBold>
            <TableHeadWithFontBold className="w-3/5 text-sm">
              保険者番号
            </TableHeadWithFontBold>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell className="min-w-48 px-6 py-4 text-sm md:w-2/5 md:max-w-none">
              {insurer.name || "-"}
            </TableCell>
            <TableCell className="w-3/5 px-6 py-4 text-sm">
              {insurer.insurerNumber || "-"}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};

const InsuredMemberHistory = ({
  insuredHistory,
  latestLostRecordIdsByMember,
  onHistoryClick,
  insurer,
}: {
  insuredHistory: InsuranceHistoryData[];
  latestLostRecordIdsByMember: LatestLostInsuranceNumberIdsByMember;
  onHistoryClick: (data: InsuranceHistoryData) => void;
  insurer: Insurer;
}) => {
  const showAlert = hasLatestLostOptionalContinued(insuredHistory);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold">被保険者</h3>
      </div>
      <HistoryTable
        history={insuredHistory}
        latestLostRecordIdsByMember={latestLostRecordIdsByMember}
        isPrimary={true}
        onRowClick={onHistoryClick}
        insurer={insurer}
      />
      {showAlert && <OptionalContinuedCertificateAlert />}
    </div>
  );
};

const DependentMemberHistory = ({
  history,
  latestLostRecordIdsByMember,
  showRelationshipNote = true,
  onHistoryClick,
  insurer,
}: {
  history: InsuranceHistoryData[];
  latestLostRecordIdsByMember: LatestLostInsuranceNumberIdsByMember;
  showRelationshipNote?: boolean;
  onHistoryClick: (data: InsuranceHistoryData) => void;
  insurer: Insurer;
}) => {
  const showAlert = hasLatestLostOptionalContinued(history);

  return (
    <div className="space-y-6">
      <HistoryTable
        history={history}
        latestLostRecordIdsByMember={latestLostRecordIdsByMember}
        isPrimary={false}
        onRowClick={onHistoryClick}
        insurer={insurer}
      />
      {showRelationshipNote && (
        <p className="text-sm text-monotone-9">
          ※続柄は被保険者から見た関係性を示すもの
        </p>
      )}
      {showAlert && <OptionalContinuedCertificateAlert />}
    </div>
  );
};

/**
 * 過去の資格情報の説明文を取得
 */
const getFormerHistoryDescription = (
  hasInsuredHistory: boolean,
  hasDependentHistory: boolean,
): string => {
  // 被保険者履歴がある場合
  if (hasInsuredHistory) {
    if (hasDependentHistory) {
      return "被保険者であった期間に扶養していたご家族の資格情報とその履歴です。";
    }
    return "被保険者であった期間の資格情報の履歴です。";
  }
  // 被扶養者履歴のみの場合
  return "被扶養者であった期間の資格情報の履歴です。";
};

const FORMER_HISTORY_NOTICE =
  "法定保存期間内の過去記録を掲載しており訂正はできませんが、退職後の行政手続きや証明書類の作成などで過去の状況を確認する際にご利用いただけます。";

const FormerInsuredMemberHistory = ({
  insuredHistory,
  dependentHistory,
  latestLostRecordIdsByMember,
  memberEligibility,
  onHistoryClick,
  insurer,
}: {
  /** 過去の被保険者としての資格履歴（現在被保険者の場合は空配列） */
  insuredHistory: InsuranceHistoryData[];
  /** 過去の被扶養者としての資格履歴 */
  dependentHistory: InsuranceHistoryData[];
  /** 加入者ごとの最新の資格喪失レコードID（資格喪失証明書ボタン表示判定用） */
  latestLostRecordIdsByMember: LatestLostInsuranceNumberIdsByMember;
  /** 現在の資格状態（被扶養者追加ボタン表示制御に使用） */
  memberEligibility: MemberEligibility;
  /** 履歴クリック時のコールバック（詳細表示用） */
  onHistoryClick: (data: InsuranceHistoryData) => void;
  /** 保険者情報 */
  insurer: Insurer;
}) => {
  const hasInsuredHistory = insuredHistory.length > 0;
  const hasDependentHistory = dependentHistory.length > 0;

  return (
    <div className="space-y-6">
      <p className="text-sm">
        {getFormerHistoryDescription(hasInsuredHistory, hasDependentHistory)}
        <br />
        {FORMER_HISTORY_NOTICE}
      </p>
      {hasInsuredHistory && (
        <InsuredMemberHistory
          insuredHistory={insuredHistory}
          latestLostRecordIdsByMember={latestLostRecordIdsByMember}
          onHistoryClick={onHistoryClick}
          insurer={insurer}
        />
      )}
      {hasDependentHistory &&
        (hasInsuredHistory ? (
          <DependentSection
            dependentHistory={dependentHistory}
            latestLostRecordIdsByMember={latestLostRecordIdsByMember}
            memberEligibility={memberEligibility}
            enableAddDependent={false} // 過去の資格情報では被扶養者の追加はできないため
            showRelationshipNote={false} // 被保険者であった期間の情報では注釈不要
            onHistoryClick={onHistoryClick}
            insurer={insurer}
          />
        ) : (
          <DependentMemberHistory
            history={dependentHistory}
            latestLostRecordIdsByMember={latestLostRecordIdsByMember}
            onHistoryClick={onHistoryClick}
            insurer={insurer}
          />
        ))}
    </div>
  );
};

export const EligibilityInformationSection = ({
  viewerMemberId,
  currentPrimary,
  isFormerInsured,
  memberEligibility,
  insuredHistory = [],
  currentDependentHistory = [],
  pastDependentHistory = [],
  latestLostRecordIdsByMember,
  currentDependents,
  insurer,
  showLostEligibilityNote,
}: {
  viewerMemberId: string;
  currentPrimary: boolean;
  isFormerInsured: boolean;
  memberEligibility: MemberEligibility;
  insuredHistory?: InsuranceHistoryData[];
  currentDependentHistory?: InsuranceHistoryData[];
  pastDependentHistory?: InsuranceHistoryData[];
  latestLostRecordIdsByMember: LatestLostInsuranceNumberIdsByMember;
  currentDependents: string[];
  insurer: Insurer;
  /** 資格喪失履歴があるかどうか（資格喪失証明書の注釈表示用） */
  showLostEligibilityNote: boolean;
}) => {
  const [selectedData, setSelectedData] = useState<InsuranceHistoryData | null>(
    null,
  );
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const handleHistoryClick = (data: InsuranceHistoryData) => {
    setSelectedData(data);
    setIsSheetOpen(true);
  };

  const handleSheetClose = () => {
    setIsSheetOpen(false);
    setSelectedData(null);
  };

  return (
    <section>
      <div className="space-y-12 border-t border-monotone-2 pt-12">
        <div className="space-y-6">
          <h2 className="text-xl font-bold">資格情報</h2>
          <Description
            isPrimary={currentPrimary}
            showLostEligibilityNote={showLostEligibilityNote}
          />
        </div>
        {currentPrimary || (insuredHistory.length > 0 && !isFormerInsured) ? ( // 最後の資格が被保険者である場合
          <>
            <InsuredMemberHistory
              insuredHistory={insuredHistory}
              latestLostRecordIdsByMember={latestLostRecordIdsByMember}
              onHistoryClick={handleHistoryClick}
              insurer={insurer}
            />
            <DependentSection
              dependentHistory={currentDependentHistory}
              latestLostRecordIdsByMember={latestLostRecordIdsByMember}
              memberEligibility={memberEligibility}
              showRelationshipNote={false} // 被保険者から見た被扶養者の続柄は自明のため注釈不要
              onHistoryClick={handleHistoryClick}
              insurer={insurer}
            />
          </>
        ) : (
          // 被扶養者の場合
          <DependentMemberHistory
            history={currentDependentHistory}
            latestLostRecordIdsByMember={latestLostRecordIdsByMember}
            onHistoryClick={handleHistoryClick}
            insurer={insurer}
          />
        )}
        {(isFormerInsured || pastDependentHistory.length > 0) && ( // 過去の資格情報がある場合
          <Accordion type="single" collapsible>
            <AccordionItem
              value="former-insured-history"
              className="border-b-0"
            >
              <AccordionTrigger className="justify-start gap-3 text-lg font-bold">
                {isFormerInsured
                  ? "被保険者であった期間の情報"
                  : "被扶養者であった期間の情報"}
              </AccordionTrigger>
              <AccordionContent>
                <FormerInsuredMemberHistory
                  insuredHistory={isFormerInsured ? insuredHistory : []}
                  dependentHistory={pastDependentHistory}
                  latestLostRecordIdsByMember={latestLostRecordIdsByMember}
                  memberEligibility={memberEligibility}
                  onHistoryClick={handleHistoryClick}
                  insurer={insurer}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
        <InsurerSection insurer={insurer} />
      </div>

      <InformationSheet
        viewerMemberId={viewerMemberId}
        data={selectedData}
        open={isSheetOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleSheetClose();
          }
        }}
        currentDependents={currentDependents}
      />
    </section>
  );
};
